import { forwardRef, useRef, type ReactNode, type UIEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDebounceValue } from "usehooks-ts";
import {
  ArrowLeft,
  BarChart3,
  ExternalLink,
  FileText,
  Film,
  ImageIcon,
  Link2,
  MapPin,
  Mic,
  Search,
  SearchX,
  UserRound,
  Video,
  X,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty-state";
import { useLocale } from "@/hooks/useLocale";
import { useSearchChatMessagesInfiniteQuery } from "@/services/(my-space)/api.chat";
import { getErrorMessage } from "@/utils/error.utils";
import type { ChatMessageType, IChatMessage } from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import {
  conversationTime,
  messageContentPreview,
  personName,
} from "../_lib/chat-labels";
import {
  MESSAGE_SEARCH_MAX_LENGTH,
  isSearchableQuery,
  normalizeSearchQuery,
  searchResultText,
  searchSnippet,
  type HighlightSegment,
} from "../_lib/message-search";

const SEARCH_DEBOUNCE_MS = 300;
const LOAD_MORE_THRESHOLD_PX = 160;

/** The icon in front of a result that isn't plain text. */
const KIND_ICONS: Partial<Record<ChatMessageType, LucideIcon>> = {
  image: ImageIcon,
  video: Video,
  file: FileText,
  voice: Mic,
  gif: Film,
  location: MapPin,
  contact: UserRound,
  poll: BarChart3,
  link: Link2,
  shortcut: ExternalLink,
};

// ── Header bar ──────────────────────────────────────────────────────────────

interface MessageSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

/** Replaces the room header while searching: back, the query field, clear. */
export const MessageSearchBar = forwardRef<HTMLInputElement, MessageSearchBarProps>(
  function MessageSearchBar({ value, onChange, onClose }, ref) {
    const { t } = useTranslation("chat");
    const localRef = useRef<HTMLInputElement | null>(null);
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t("search.close")}
          title={t("search.close")}
          className="text-muted-foreground"
        >
          <ArrowLeft className="size-4 rtl-flip" />
        </Button>
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={(el) => {
              localRef.current = el;
              if (typeof ref === "function") ref(el);
              else if (ref) ref.current = el;
            }}
            autoFocus
            value={value}
            maxLength={MESSAGE_SEARCH_MAX_LENGTH}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                onClose();
              }
            }}
            placeholder={t("search.placeholder")}
            aria-label={t("search.placeholder")}
            className="h-9 bg-surface ps-9 pe-9"
          />
          {value ? (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => {
                onChange("");
                localRef.current?.focus();
              }}
              aria-label={t("search.clear")}
              title={t("search.clear")}
              className="absolute end-1.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              <X className="size-3.5" />
            </Button>
          ) : null}
        </div>
      </div>
    );
  },
);

// ── Results ─────────────────────────────────────────────────────────────────

interface MessageSearchResultsProps {
  conversationId: number;
  /** The raw field value; debounced and trimmed here. */
  query: string;
  myUserId: number | undefined;
  onOpen: (message: IChatMessage) => void;
  onClose: () => void;
}

/** Fills the message area while searching. */
export const MessageSearchResults = ({
  conversationId,
  query,
  myUserId,
  onOpen,
  onClose,
}: MessageSearchResultsProps) => {
  const { t } = useTranslation("chat");
  const [debounced] = useDebounceValue(normalizeSearchQuery(query), SEARCH_DEBOUNCE_MS);
  const searchable = isSearchableQuery(debounced);
  const search = useSearchChatMessagesInfiniteQuery(
    { conversationId, q: debounced },
    { skip: !searchable },
  );
  const results = searchable ? (search.data?.pages.flatMap((p) => p.data) ?? []) : [];

  const loadMore = () => {
    if (search.hasNextPage && !search.isFetchingNextPage && !search.isError) {
      void search.fetchNextPage();
    }
  };
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) loadMore();
  };

  const body = (() => {
    if (!searchable) {
      return (
        <SearchEmpty
          icon={<Search />}
          title={t("search.idle_title")}
          description={t("search.idle_hint")}
        />
      );
    }
    if (search.isLoading || (search.isFetching && !search.data)) return <ResultsSkeleton />;
    if (search.isError && !results.length) {
      return (
        <SearchEmpty
          icon={<SearchX />}
          title={t("search.error")}
          description={getErrorMessage(search.error, t("search.error_hint"))}
          action={
            <Button variant="outline" size="sm" onClick={() => void search.refetch()}>
              {t("page.try_again")}
            </Button>
          }
        />
      );
    }
    if (!results.length) {
      return (
        <SearchEmpty
          icon={<SearchX />}
          title={t("search.no_results")}
          description={t("search.no_results_hint", { query: debounced })}
        />
      );
    }
    return (
      <>
        <ul className="flex flex-col py-1" aria-label={t("search.results")}>
          {results.map((m) => (
            <li key={m.id} className="px-2">
              <SearchResultRow
                message={m}
                query={debounced}
                isMine={m.sender?.id === myUserId}
                onOpen={() => onOpen(m)}
              />
            </li>
          ))}
        </ul>
        {search.isFetchingNextPage ? (
          <ResultsSkeleton rows={2} />
        ) : search.isError ? (
          <div className="flex flex-col items-center gap-2 px-4 py-3 text-center">
            <span className="text-xs text-muted-foreground">{t("search.error")}</span>
            <Button variant="outline" size="xs" onClick={() => void search.fetchNextPage()}>
              {t("page.try_again")}
            </Button>
          </div>
        ) : search.hasNextPage ? (
          <div className="flex justify-center px-4 py-3">
            <Button variant="ghost" size="sm" onClick={loadMore}>
              {t("search.load_more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  })();

  return (
    <div
      className="absolute inset-0 z-30 overflow-y-auto bg-background"
      onScroll={onScroll}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      aria-busy={search.isFetching || undefined}
    >
      {body}
    </div>
  );
};

const SearchResultRow = ({
  message: m,
  query,
  isMine,
  onOpen,
}: {
  message: IChatMessage;
  query: string;
  isMine: boolean;
  onOpen: () => void;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const name = isMine ? t("search.you") : personName(t, m.sender);
  const KindIcon = KIND_ICONS[m.type];
  const text =
    searchResultText(m, query) ||
    messageContentPreview(t, { ...m, attachmentCount: m.attachments.length });
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-3 rounded-sm px-2 py-2.5 text-start transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-hidden"
    >
      <ChatAvatar name={personName(t, m.sender)} imageUrl={m.sender?.profileImage} size="md" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{name}</span>
          <span className="ms-auto shrink-0 text-[11px] text-muted-foreground">
            {conversationTime(t, formatDate, m.createdAt)}
          </span>
        </span>
        <span className="flex min-w-0 items-start gap-1.5 text-sm text-muted-foreground">
          {KindIcon ? <KindIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden /> : null}
          <span className="line-clamp-2 break-words">
            {/* User content keeps its own direction (English in an Arabic UI). */}
            <bdi>
              <Highlighted segments={searchSnippet(text, query)} />
            </bdi>
          </span>
        </span>
      </span>
    </button>
  );
};

/** Matches as <mark>; plain text otherwise — never HTML. */
export const Highlighted =({ segments }: { segments: HighlightSegment[] }) => (
  <>
    {segments.map((s, i) =>
      s.match ? (
        <mark key={i} className="rounded-xs bg-primary-muted px-0.5 font-semibold text-primary">
          {s.text}
        </mark>
      ) : (
        <span key={i}>{s.text}</span>
      ),
    )}
  </>
);

const SearchEmpty = ({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) => (
  <Empty className="h-full border-0 p-6">
    <EmptyHeader>
      <EmptyMedia variant="icon">{icon}</EmptyMedia>
      <EmptyTitle className="text-base">{title}</EmptyTitle>
      <EmptyDescription>{description}</EmptyDescription>
    </EmptyHeader>
    {action}
  </Empty>
);

const ResultsSkeleton = ({ rows = 6 }: { rows?: number }) => (
  <div className="flex flex-col gap-1 px-4 py-2" aria-hidden>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-start gap-3 py-2">
        <Skeleton className="size-10 shrink-0 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex justify-between gap-4">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3 w-10" />
          </div>
          <Skeleton className="h-3.5" style={{ width: `${55 + ((i * 17) % 40)}%` }} />
        </div>
      </div>
    ))}
  </div>
);
